import gql from "graphql-tag";
import {GqlModel, GqlModelOperator, GqlMutationModel, GqlQueryModel} from "../graphql/graphql.model";
import {jsonProperty} from "../json.model";

export class CDFBulkOrderItemCandidate extends GqlModel {
  @jsonProperty("string") id: string;
  @jsonProperty("string") orderNumber: string;
  @jsonProperty("string") orderDate: string;
  @jsonProperty("number") quantity: number;
  @jsonProperty("number") quantityAvailable: number;
}

export class CDFItemCandidate extends GqlModel {
  @jsonProperty("string") id: string;
  @jsonProperty("string") deviceKey: string;
  @jsonProperty("string") productNumber: string;
  @jsonProperty("string") productDescription: string;
  @jsonProperty([CDFBulkOrderItemCandidate]) availableBulkOrderItems: CDFBulkOrderItemCandidate[];
}

export abstract class CDFItem extends GqlModel {
  @jsonProperty("number") rowId: number;
  @jsonProperty("string") productNumber: string;
  @jsonProperty("string") productDescription: string;
  @jsonProperty("string") deviceId: string;
  @jsonProperty("string") deviceKey: string;

  bulkOrderAvailable: boolean = false;
  bulkOrderItemId: string;
  orderNumber: string;
  orderDate: string;
  availableBulkOrderItems: CDFBulkOrderItemCandidate[];
  abstract candidates: CDFItemCandidate[];

  isDifferent(candidate: CDFItemCandidate): boolean {
    if (!candidate) return !this.deviceId && !this.deviceKey;
    return !(candidate.id === this.deviceId && candidate.deviceKey === this.deviceKey);
  }

  abstract autoAssociateDevice(): boolean;

  abstract associateDevice(candidate: CDFItemCandidate): void;

  abstract clearDevice(): void;

  abstract associateBulkOrderItem(bulkOrderLineItem: CDFBulkOrderItemCandidate): void;

  abstract clearBulkOrderItem(): void;
}

export class CDFAccessoryCandidate extends CDFItemCandidate {}

export class CDFAccessory extends CDFItem {
  @jsonProperty([CDFAccessoryCandidate]) candidates: CDFAccessoryCandidate[];

  bundled: boolean = false;
  associated: boolean = false;

  associateBulkOrderItem(bulkOrderLineItem: CDFBulkOrderItemCandidate) {
    this.bulkOrderItemId = bulkOrderLineItem.id;
    this.orderNumber = bulkOrderLineItem.orderNumber;
    this.orderDate = bulkOrderLineItem.orderDate;
  }

  clearBulkOrderItem() {
    this.bulkOrderAvailable = false;
    this.availableBulkOrderItems = [];
    this.bulkOrderItemId = undefined;
    this.orderNumber = undefined;
    this.orderDate = undefined;
  }

  associateDevice(candidate: CDFAccessoryCandidate) {
    this.associated = true;
    this.bundled = false;
    this.deviceId = candidate.id;
    this.deviceKey = candidate.deviceKey;
    this.clearBulkOrderItem();
    this.bulkOrderAvailable = candidate.availableBulkOrderItems.length > 0;
    this.availableBulkOrderItems = [...candidate.availableBulkOrderItems];
  }

  autoAssociateDevice(): boolean {
    if (this.candidates.length !== 1) {
      return false;
    }

    this.associateDevice(this.candidates[0]);

    return true;
  }

  clearDevice() {
    this.associated = false;
    this.bundled = false;
    this.deviceId = undefined;
    this.deviceKey = undefined;
  }
}

export class CDFDispositionInfo extends GqlModel {
  @jsonProperty("number") id: number;
  @jsonProperty("string") description: string;
  @jsonProperty("string") key: string;
  @jsonProperty("boolean") isReposition: boolean;
  @jsonProperty("boolean") isFutureFleet: boolean;
  @jsonProperty(["number"]) dispositionIds: number[];
}

export class CDFBundledAccessories extends GqlModel {
  @jsonProperty("string") productNumber: string;
  @jsonProperty("string") productDescription: string;
}

export class CDFAssetCandidate extends CDFItemCandidate {
  @jsonProperty("number") dispositionId: number;
  @jsonProperty([CDFBundledAccessories]) bundledAccessories: CDFBundledAccessories[];
}

export class CDFAsset extends CDFItem {
  @jsonProperty("string") floorMapId: string;
  @jsonProperty("string") serialNumber: string;
  @jsonProperty("string") assetTag: string;
  @jsonProperty("string") manufacturer: string;
  @jsonProperty("string") location: string;
  @jsonProperty("string") site: string;
  @jsonProperty("string") building: string;
  @jsonProperty("string") floor: string;
  @jsonProperty(CDFDispositionInfo) dispositionInfo: CDFDispositionInfo;
  @jsonProperty("number") dispositionId: number;
  @jsonProperty("string") itemNumber: string;
  @jsonProperty("string") replacedByItemNumber: string;
  @jsonProperty("boolean") isUpdate: boolean;
  @jsonProperty([CDFAccessory]) accessories: CDFAccessory[];
  @jsonProperty([CDFAssetCandidate]) candidates: CDFAssetCandidate[];
  @jsonProperty(["string"]) siteIds: string[];
  @jsonProperty("string") /** Portico/SOLD Project ID */ projectId: string;
  @jsonProperty(CDFAsset.getExtraSearchString) extraSearchString: string;

  associated: boolean = false;

  constructor(data: any) {
    super(data);

    this.autoAssociateDevice();
  }

  static getExtraSearchString(value: any, data: any, instance: CDFAsset): string {
    const values = [];

    for (const accessory of instance.accessories) {
      if (accessory.productNumber) values.push(accessory.productNumber);
      if (accessory.productDescription) values.push(accessory.productDescription);
    }

    return values.join(" ");
  }

  associateBulkOrderItem(bulkOrderLineItem: CDFBulkOrderItemCandidate) {
    this.bulkOrderItemId = bulkOrderLineItem.id;
    this.orderNumber = bulkOrderLineItem.orderNumber;
    this.orderDate = bulkOrderLineItem.orderDate;
  }

  clearBulkOrderItem() {
    this.bulkOrderAvailable = false;
    this.availableBulkOrderItems = [];
    this.bulkOrderItemId = undefined;
    this.orderNumber = undefined;
    this.orderDate = undefined;
  }

  associateDevice(candidate: CDFAssetCandidate) {
    this.associated = true;
    this.deviceId = candidate.id;
    this.deviceKey = candidate.deviceKey;
    this.clearBulkOrderItem();
    this.bulkOrderAvailable = candidate.availableBulkOrderItems.length > 0;
    this.availableBulkOrderItems = [...candidate.availableBulkOrderItems];
    this.updateBundledAccessoryAssociations();
  }

  autoAssociateDevice(): boolean {
    if (this.isUpdate) return false;

    if (this.candidates.length !== 1) {
      return false;
    }

    const bundledAccessories = [...this.candidates[0].bundledAccessories];

    for (const accessory of this.accessories) {
      const index = bundledAccessories.findIndex(bundledAccessory =>
        bundledAccessory.productNumber === accessory.productNumber);
      if (index === -1 && accessory.candidates.length !== 1) return false;
      bundledAccessories.splice(index, 1);
    }

    this.associateDevice(this.candidates[0]);

    for (const accessory of this.accessories) {
      accessory.autoAssociateDevice();
    }

    return true;
  }

  clearDevice() {
    this.associated = false;
    this.deviceId = undefined;
    this.deviceKey = undefined;
    this.clearBulkOrderItem();
    this.updateBundledAccessoryAssociations();

    for (const accessory of this.accessories) {
      accessory.clearBulkOrderItem();
    }
  }

  updateBundledAccessoryAssociations() {
    const device = this.candidates.find(it => it.id === this.deviceId);
    const bundledAccessories = Array.prototype.slice.call((device ? device.bundledAccessories : undefined) || []);
    const bundledAccessoryIndexByProductNumber = [];
    for (let i = 0; i < bundledAccessories.length; i++) {
      bundledAccessoryIndexByProductNumber[bundledAccessories[i].productNumber] = i;
    }
    for (const accessory of this.accessories) {
      if (!accessory.deviceId) {
        const index = bundledAccessoryIndexByProductNumber[accessory.productNumber];
        if (index >= 0) {
          accessory.bundled = true;
          accessory.associated = true;
          bundledAccessories.splice(index, 1);
        } else if (accessory.productNumber.includes("HP-NPI-Access")) {
          // accessory.bundled = true;
          accessory.associated = true;
          accessory.deviceKey = this.deviceKey;
        } else {
          accessory.bundled = false;
          accessory.associated = false;
          if (this.deviceId) accessory.autoAssociateDevice();
        }
      }
    }
  }
}

export class CDFGqlModel extends GqlQueryModel {
  static mapper: GqlModelOperator<CDFGqlModel> = GqlModel.buildMapper(CDFGqlModel, "cdf");

  static query: any = gql`
fragment bulkOrderDetails on BulkOrderLineItem {
  id
  orderNumber
  orderDate
  quantity
  quantityAvailable
}

query getCdf($projectId: ID, $siteId: ID) {
  cdf: cdf(projectId: $projectId, siteId: $siteId) {
    assets {
      rowId
      productNumber
      productDescription
      floorMapId
      serialNumber
      assetTag
      manufacturer
      location
      site
      building
      floor
      dispositionId
      itemNumber
      replacedByItemNumber
      projectId
      siteIds
      isUpdate
      bulkOrderItemId
      orderNumber
      orderDate
      dispositionInfo {
        id
        description
        key
        isReposition
        isFutureFleet
        dispositionIds
      }
      accessories {
        rowId
        productNumber
        productDescription
        candidates {
          id
          deviceKey
          productNumber
          productDescription
          availableBulkOrderItems {
            ...bulkOrderDetails
          }
        }
      }
      candidates {
        id
        deviceKey
        productNumber
        productDescription
        dispositionId
        bundledAccessories {
          productNumber
          productDescription
        }
        availableBulkOrderItems {
          ...bulkOrderDetails
        }
      }
    }
    hash
    isMigrationActive
  }
}
  `;

  @jsonProperty([CDFAsset]) assets: CDFAsset[];
  @jsonProperty("string") hash: string;
  @jsonProperty("boolean") isMigrationActive: boolean;

  static variables(projectId: string, siteId: string) {
    return {projectId, siteId};
  }
}

export class CDFCandidateInput extends GqlModel {
  @jsonProperty("string") id: string;
}

export class CDFAccessoryInput extends GqlModel {
  @jsonProperty("number") rowId: number;
  @jsonProperty("string") productNumber: string;
  @jsonProperty("string") productDescription: string;
  @jsonProperty("string") deviceId: string;
  @jsonProperty([CDFCandidateInput]) candidates: CDFCandidateInput[];
  @jsonProperty("string") bulkOrderItemId: string;
  @jsonProperty("string") orderNumber: string;
  @jsonProperty("string") orderDate: string;

  constructor(accessory: CDFAccessory) {
    super(accessory);

    if (!accessory.bulkOrderItemId) {
      this.orderNumber = undefined;
      this.orderDate = undefined;
    }
  }
}

export class CDFAssetInput extends GqlModel {
  @jsonProperty("number") rowId: number;
  @jsonProperty("string") productNumber: string;
  @jsonProperty("string") productDescription: string;
  @jsonProperty("string") deviceId: string;
  @jsonProperty("string") floorMapId: string;
  @jsonProperty("string") serialNumber: string;
  @jsonProperty("string") assetTag: string;
  @jsonProperty("string") manufacturer: string;
  @jsonProperty("string") location: string;
  @jsonProperty("string") site: string;
  @jsonProperty(["string"]) siteIds: string[];
  @jsonProperty("string") building: string;
  @jsonProperty("string") floor: string;
  @jsonProperty("number") dispositionId: number;
  @jsonProperty("string") itemNumber: string;
  @jsonProperty("string") replacedByItemNumber: string;
  @jsonProperty([CDFAccessoryInput]) accessories: any[];
  @jsonProperty([CDFCandidateInput]) candidates: CDFCandidateInput[];

  @jsonProperty("string") bulkOrderItemId: string;
  @jsonProperty("string") orderNumber: string;
  @jsonProperty("string") orderDate: string;

  constructor(asset: CDFAsset) {
    super(asset);

    if (!asset.bulkOrderItemId) {
      this.orderNumber = undefined;
      this.orderDate = undefined;
    }
  }
}

export class CDFInputResultGqlModel extends GqlModel {
  @jsonProperty("string") id: string;
}

export class CDFInputDataModel extends GqlModel {
  @jsonProperty([CDFAssetInput]) assets: CDFAssetInput[];
  @jsonProperty("string") hash: string;
}

export class CDFInputGqlModel extends GqlMutationModel {
  static mapper: GqlModelOperator<CDFInputResultGqlModel> = GqlModel.buildMapper(CDFInputResultGqlModel, "cdf");

  static query: any = gql`
mutation updateCdf($cdf: CDFInput, $projectId: ID) {
  cdf: cdf(cdf: $cdf, projectId: $projectId) {
    id
  }
}
`;

  @jsonProperty(CDFInputDataModel) cdf: CDFInputDataModel;
  @jsonProperty("string") projectId: string;
  @jsonProperty("boolean") removeAssets: boolean;
}
